import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { ThemeButton, useModalContext, AcceptationDialog } from '../'
import { dataToXLS, getFormattedTime } from '../../core/utils'
import { ExportSomethingDialog } from './export-something.dialog'

const DEFAULT_GET_ALL_DATA = () => new Promise((resolve) => resolve([]))

/**
 * @typedef {object} ColumnExportConfig
 * @property {string} key - La clave del campo en los objetos de datos.
 * @property {string} label - El encabezado de la columna en el archivo Excel.
 * @property {string} [type] - El tipo de dato para aplicar formato especial (ej. 'date', 'currency', 'combo').
 */

/**
 * @typedef {object} ExportToXLSBtnProps
 * @property {boolean} [exportSelectionEnabled=true] - Si es `true`, muestra el botón para exportar las filas seleccionadas.
 * @property {boolean} [exportAllEnabled=true] - Si es `true`, muestra el botón para exportar todos los datos.
 * @property {object[]} [selectedRows=[]] - El array de objetos de datos que representan las filas actualmente seleccionadas.
 * @property {ColumnExportConfig[]} [columnsExport=[]] - La configuración de las columnas a incluir en el archivo Excel exportado.
 * @property {function(): Promise<object[]>} [getAllData=DEFAULT_GET_ALL_DATA] - Una función asíncrona que devuelve todos los datos disponibles para la exportación completa.
 * @property {string} [titleRoot='export_'] - El prefijo para el nombre del archivo Excel generado.
 * @property {string} [headerNamespace='applications'] - El namespace de i18n para traducir los encabezados de las columnas.
 * @property {number} [totalRecords] - El número total de registros disponibles. Se usa para validar el límite de exportación.
 * @property {number} [totalRecordsLimit=0] - El límite máximo de registros que se pueden exportar. Si se excede, se muestra un diálogo de advertencia. Un valor de `0` significa sin límite.
 */

/**
 * Un componente que renderiza botones para exportar datos a un archivo Excel (.xlsx).
 * 
 * Proporciona dos opciones de exportación:
 * 1. **Exportar Selección**: Exporta solo las filas seleccionadas por el usuario.
 * 2. **Exportar Todo**: Exporta todos los datos disponibles, obtenidos a través de la función `getAllData`.
 * 
 * El componente gestiona estados de carga, valida límites de exportación y muestra diálogos
 * informativos cuando no hay datos seleccionados o cuando se excede el límite de registros.
 * Utiliza `react-intl` para la internacionalización y Redux para acceder a los combos de datos.
 * 
 * @component
 * @example
 * const MyDataTable = () => {
 *   const [selectedRows, setSelectedRows] = useState([
 *     { id: 1, name: 'Producto A', price: 100 },
 *     { id: 2, name: 'Producto B', price: 200 },
 *   ]);
 * 
 *   const fetchAllData = async () => {
 *     const response = await fetch('/api/products');
 *     return response.json();
 *   };
 * 
 *   const columns = [
 *     { key: 'id', label: 'ID' },
 *     { key: 'name', label: 'Nombre' },
 *     { key: 'price', label: 'Precio', type: 'currency' },
 *   ];
 * 
 *   // Este componente debe estar envuelto en <IntlProvider> y tener acceso al store de Redux.
 *   return (
 *     <ExportToXLSBtn
 *       selectedRows={selectedRows}
 *       columnsExport={columns}
 *       getAllData={fetchAllData}
 *       titleRoot="productos_"
 *       headerNamespace="products"
 *       totalRecords={1500}
 *       totalRecordsLimit={1000}
 *     />
 *   );
 * };
 * 
 * @param {ExportToXLSBtnProps} props - Las props del componente ExportToXLSBtn.
 */
export const ExportToXLSBtn = ({
  exportSelectionEnabled = true,
  exportAllEnabled = true,
  selectedRows = [],
  columnsExport = [],
  getAllData = DEFAULT_GET_ALL_DATA,
  titleRoot = 'export_',
  headerNamespace = 'applications',
  totalRecords,
  totalRecordsLimit = 0,
}) => {
  const intl = useIntl()
  const combos = useSelector((state) => state.combos)
  const [loading, setLoading] = useState(false)

  const {
    actions: { open },
  } = useModalContext()

  const mainExport = async (data) => {
    const fileName = `${titleRoot}${getFormattedTime()}.xlsx`
    await dataToXLS(data, columnsExport, combos, intl, headerNamespace, fileName)
  }

  const handleExport = async () => {
    if (!selectedRows?.length) {
      open({ Component: ExportSomethingDialog })
      return
    }

    setLoading(true)
    try {
      await mainExport(selectedRows)
    } catch (error) {
      console.error('Error al exportar la selección:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleExportAll = async () => {
    if (totalRecordsLimit && totalRecords > totalRecordsLimit) {
      open({
        Component: AcceptationDialog,
        data: {
          title: intl.formatMessage({ id: 'global.exportAll.limitExcededDialog.title' }),
          text: intl.formatMessage(
            { id: 'global.exportAll.limitExcededDialog.description' },
            { total: totalRecords, limit: totalRecordsLimit }
          ),
          yesText: intl.formatMessage({ id: 'global.close' }),
        },
      })
      return
    }

    setLoading(true)
    try {
      const recievedData = await getAllData()
      await mainExport(recievedData)
    } catch (error) {
      console.error('Error al exportar todos los datos:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {exportSelectionEnabled ? (
        <ThemeButton
          color="primary"
          labelM="sm"
          onClick={handleExport}
          style={{ marginRight: '1em' }}
          loading={loading}
          disabled={loading}
        >
          {intl.formatMessage({ id: 'global.exportSelected' })} ({selectedRows.length})
        </ThemeButton>
      ) : null}
      {exportAllEnabled ? (
        <ThemeButton
          color="primary"
          labelM="sm"
          loading={loading}
          onClick={handleExportAll}
          disabled={loading}
        >
          {intl.formatMessage({ id: 'global.exportAll' })}
        </ThemeButton>
      ) : null}
    </>
  )
}
